import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

/**
 * App information.
 */
export interface AppInfo {
  name: string;
  id: string;
  build: string;
  version: string;
}

/**
 * App state change event data.
 */
export interface AppStateChangeData {
  isActive: boolean;
}

/**
 * App URL open event data.
 */
export interface AppUrlOpenData {
  url: string | undefined;
}

/**
 * Listener handle for app events.
 */
export interface AppListenerHandle {
  remove: () => Promise<void>;
}

/**
 * Gets the app information.
 * On native platforms, returns app metadata (name, id, build, version).
 * On web, returns generic app info.
 *
 * @returns App information
 */
export async function getAppInfo(): Promise<AppInfo> {
  if (!Capacitor.isNativePlatform()) {
    return {
      name: "CBH Mobile App",
      version: "",
      build: "",
      id: "",
    };
  }

  return await App.getInfo();
}

/**
 * Adds a listener for app state changes (foreground/background).
 * On native platforms, listens for state transitions.
 * On web, returns a no-op listener handle.
 *
 * @param callback - Callback invoked when app state changes
 * @returns Listener handle
 */
export async function addAppStateChangeListener(
  callback: (data: AppStateChangeData) => void
): Promise<AppListenerHandle> {
  if (!Capacitor.isNativePlatform()) {
    return {
      remove: async () => {
        // No-op for web platform
      },
    };
  }

  return await App.addListener("appStateChange", callback);
}

/**
 * Adds a listener for app resume events (returning from background).
 * On native platforms, listens for app resume.
 * On web, returns a no-op listener handle.
 *
 * @param callback - Callback invoked when app resumes
 * @returns Listener handle
 */
export async function addAppResumeListener(callback: () => void): Promise<AppListenerHandle> {
  if (!Capacitor.isNativePlatform()) {
    return {
      remove: async () => {
        // No-op for web platform
      },
    };
  }

  return await App.addListener("resume", callback);
}

/**
 * Adds a listener for app URL open events (deep links).
 * On native platforms, listens for URL opens.
 * On web, returns a no-op listener handle.
 *
 * @param callback - Callback invoked when URL is opened
 * @returns Listener handle
 */
export async function addAppUrlOpenListener(
  callback: (data: AppUrlOpenData) => void
): Promise<AppListenerHandle> {
  if (!Capacitor.isNativePlatform()) {
    return {
      remove: async () => {
        // No-op for web platform
      },
    };
  }

  return await App.addListener("appUrlOpen", callback);
}
